<template>
  <div class="documentation-attachments">
    <strong>Vedlagte filer ({{ totalAttachmentCount }}):</strong>

    <!-- Handle new grouped structure -->
    <div v-if="attachmentGroups && attachmentGroups.length > 0">
      <div
        v-for="group in attachmentGroups"
        :key="group.supplierRequestId"
        class="documentation-attachments__group"
      >
        <div
          v-if="group.description"
          class="documentation-attachments__group-description"
        >
          <strong>{{ group.description }}</strong>
        </div>
        <ul class="documentation-attachments__file-list">
          <li
            v-for="file in group.attachments"
            :key="file.attachmentId"
            class="documentation-attachments__file-item"
          >
            <ServiceRecordDownloader
              :text="file.fileName"
              :attachment-id="file.attachmentId"
              :supplier-id="supplierId"
              :attachment-uri="file.attachmentUri || undefined"
            />
          </li>
        </ul>
      </div>
    </div>

    <!-- Handle legacy structure for backward compatibility -->
    <ul
      v-else-if="legacyAttachments && legacyAttachments.length > 0"
      class="documentation-attachments__file-list"
    >
      <li
        v-for="file in legacyAttachments"
        :key="file.attachmentId"
        class="documentation-attachments__file-item"
      >
        <ServiceRecordDownloader
          :text="file.fileName"
          :attachment-id="file.attachmentId"
          :supplier-id="supplierId"
          :attachment-uri="file.attachmentUri || undefined"
        />
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { ServiceOffer } from "@/custom-types/serviceOffers";
import ServiceRecordDownloader from "./ServiceRecordDownloader.vue";

interface Props {
  attachmentGroups?: ServiceOffer["supplierRequestAttachmentGroups"];
  legacyAttachments?: ServiceOffer["supplierRequestAttachments"]; // For backward compatibility
  supplierId: number;
}

const props = defineProps<Props>();

const totalAttachmentCount = computed(() => {
  if (props.attachmentGroups) {
    return props.attachmentGroups.reduce(
      (total, group) => total + group.attachments.length,
      0,
    );
  }
  return props.legacyAttachments?.length || 0;
});
</script>

<style lang="scss" scoped>
.documentation-attachments {
  strong {
    display: block;
    font-weight: 600;
    color: $color-text-primary;
    margin-bottom: 0.5rem;
  }

  &__group {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__group-description {
    margin-bottom: 0.5rem;

    strong {
      font-size: 0.875rem;
      color: $color-text-secondary;
      font-weight: 500;
    }
  }

  &__file-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__file-item {
    display: block;
    margin-bottom: 0.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
